import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { login, logout } from "../services/authService";
import { getEmpProfile } from "../services/employeeService";
import api from "../services/apiService";
import apiRoutes from "../services/apiRoutes";
import { getActorType, setGlobalAccessToken } from "../services/tokenService";
import Swal from "sweetalert2";
import theme from "../theme/theme";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmployeeActive, setIsEmployeeActive] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [accessToken, setAccessToken] = useState(null);
  // hierarchyRole is telling if user subordinates , or if he superior or both.
  const [hierarchyRole, setHierarchyRole] = useState("");
  const [isLeader, setIsLeader] = useState(false);
  const [announcementModal, setAnnouncementModal] = useState(false);
  const navigate = useNavigate();
  const showErrorAlert = (text) => {
    Swal.fire({
      text: text,
      confirmButtonText: "حسنا",
      confirmButtonColor: theme.colors.primary,
    });
  };

  // Map the V2 /employee/profile DTO to the userInfo shape the app already consumes.
  const buildUserInfo = (profile) => ({
    empIdNumber: profile.idNumber,
    empEmail: profile.email,
    empId: profile.employeeId,
    userType: profile.empType,
    empName: profile.name,
    isEmpActive: profile.isActive,
    isEmpCreateAccount: profile.isEmpCreateAccount,
    hierarchyRole: profile.hierarchyRole,
    isLeader: profile.isLeader,
  });

  // Fetch the profile and hydrate auth state from it. Returns the profile.
  const hydrateFromProfile = async () => {
    const profile = await getEmpProfile();
    const info = buildUserInfo(profile);
    setUserInfo(info);
    setIsAuthenticated(true);
    setHierarchyRole(info.hierarchyRole || "");
    setIsLeader(info.isLeader);
    setIsEmployeeActive(info.isEmpActive);
    localStorage.setItem("hierarchyRole", info.hierarchyRole || "");
    localStorage.setItem("isLeader", info.isLeader);
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("isEmpActive", info.isEmpActive);
    return info;
  };

  const clearSession = () => {
    setAccessToken(null);
    setGlobalAccessToken(null);
    setIsAuthenticated(false);
    setUserInfo({});
    localStorage.removeItem("hierarchyRole");
    localStorage.removeItem("isLeader");
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("isEmpActive");
    localStorage.removeItem("accessTokenExpiry");
  };

  // --------------------------
  // 1. Login method
  // --------------------------
  const empLogin = async (idNumber, password) => {
    try {
      setIsLoading(true);
      // V2 login returns only { accessToken, expiresAt }; the HttpOnly refresh cookie is set by the server.
      const response = await login(idNumber, password);
      if (response.result && response.result.accessToken) {
        const { accessToken, expiresAt } = response.result;
        setAccessToken(accessToken);
        setGlobalAccessToken(accessToken);
        if (expiresAt) localStorage.setItem("accessTokenExpiry", expiresAt);

        // User identity/roles now come from the profile, not the login response.
        const info = await hydrateFromProfile();

        if (info.isEmpActive) {
          setAnnouncementModal(true);
          navigate("/");
        } else {
          navigate("/not-active");
        }
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.errorMessages?.[0] ||
        error?.message ||
        "حدث خطأ أثناء تسجيل الدخول";
      showErrorAlert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // --------------------------
  // 2. Logout method
  // --------------------------
  const empLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await logout();
      clearSession();
      navigate("/loginPage", { replace: true });
    } catch (error) {
      clearSession();
      navigate("/loginPage", { replace: true });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // --------------------------
  // 3. Get employee info
  // --------------------------
  const empInfo = async () => {
    try {
      const empId = userInfo?.empId;
      if (!empId) return null;
      const data = await getEmpProfile();
      return data;
    } catch (error) {
      throw error;
    }
  };

  // --------------------------
  // 4. Silent refresh method
  // --------------------------
  const silentRefresh = async () => {
    try {
      // Refresh token rides the HttpOnly cookie; body only carries the actor type.
      const refreshResponse = await api.post(apiRoutes.refreshToken, {
        actorType: getActorType(),
      });
      const { accessToken, expiresAt } = refreshResponse.result;
      setAccessToken(accessToken);
      setGlobalAccessToken(accessToken);
      if (expiresAt) localStorage.setItem("accessTokenExpiry", expiresAt);
      // Token no longer carries identity/roles — pull them from the profile.
      await hydrateFromProfile();
    } catch (error) {
      clearSession();
    } finally {
      setIsLoading(false);
    }
  };

  // --------------------------
  // 5. useEffect to check session on page load
  // --------------------------
  useEffect(() => {
    const wasLoggedIn = localStorage.getItem("loggedIn");
    if (!wasLoggedIn) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        await silentRefresh();
      } catch (error) {
        clearSession();
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // --------------------------
  // 7. Context value
  // --------------------------
  return (
    <AuthContext.Provider
      value={{
        userInfo,
        isAuthenticated,
        accessToken,
        isLoading,
        hierarchyRole,
        isLeader,
        empLogin,
        empLogout,
        empInfo,
        setAccessToken,
        setIsAuthenticated,
        announcementModal,
        setAnnouncementModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
